// Planilla de aportes IESS del mes (sobre el rol del período).
// Nómina → IESS y Tributario.
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { AnexoDescarga } from './AnexoDescarga';
import { MESES, TipoRolSelect, fecha, fieldCls, money, nomRpc, type PlanillaIessData } from './shared';
import { ExportarBotones, type Celda } from '@/lib/utils/exportarTabla';
import { fechaLocal } from '@/lib/utils/fecha';

const HOY = new Date();

interface SutDecimoRow {
  tipo_id: string | null; cedula: string; nombres: string; cargo: string | null;
  fecha_ingreso: string | null; remuneracion: number | null;
  decimo_tercero: number; decimo_cuarto: number; forma_pago: string;
}
interface SutDecimosData {
  empresa: { ruc: string | null; razon_social: string | null; representante_legal: string | null };
  anio: number; mes: number | null; empleados: SutDecimoRow[];
}

export function PlanillasIess() {
  const [anio, setAnio] = useState(HOY.getFullYear());
  const [mes, setMes] = useState(HOY.getMonth() + 1);
  const [tipo, setTipo] = useState('');
  const { data, isFetching } = useQuery<PlanillaIessData>({
    queryKey: ['nom-planilla-iess', anio, mes, tipo],
    queryFn: () => nomRpc<PlanillaIessData>('nom_planilla_iess', { _anio: anio, _mes: mes, _tipo: tipo || null }),
  });
  const { data: sut } = useQuery<SutDecimosData>({
    queryKey: ['nom-sut-decimos', anio, mes],
    queryFn: () => nomRpc<SutDecimosData>('nom_sut_decimos', { _anio: anio, _mes: mes }),
  });
  const sutFilas = sut?.empleados ?? [];
  const sutTot = (k: 'decimo_tercero' | 'decimo_cuarto') => sutFilas.reduce((s, f) => s + Number(f[k]), 0);
  const sutHeaders = ['N°', 'Tipo ID', 'Identificación', 'Apellidos y nombres', 'Cargo', 'Fecha ingreso', 'Remuneración', 'Décimo tercero', 'Décimo cuarto', 'Forma de pago'];
  const sutMoney = [6, 7, 8];
  const sutExport: Celda[][] = [
    ...sutFilas.map((f, i): Celda[] => [
      i + 1, f.tipo_id ?? 'CEDULA', f.cedula, f.nombres, f.cargo ?? '', f.fecha_ingreso ? fecha(f.fecha_ingreso) : '',
      Number(f.remuneracion ?? 0), Number(f.decimo_tercero), Number(f.decimo_cuarto), f.forma_pago,
    ]),
    ['', '', '', 'TOTAL', '', '', '', sutTot('decimo_tercero'), sutTot('decimo_cuarto'), ''],
  ];

  const filas = data?.empleados ?? [];
  const pct = data?.porcentajes;
  const tot = (k: 'base' | 'ap_personal' | 'ap_patronal' | 'iece' | 'secap' | 'total') =>
    filas.reduce((s, f) => s + Number(f[k]), 0);

  // La planilla trae una línea por afiliado. Si la empresa paga por período
  // corto, cada línea consolida varios roles del mes: sólo entonces vale la
  // pena mostrar la columna, para que nadie crea que falta gente.
  const consolidada = filas.some((f) => Number(f.periodos) > 1);

  const headersExp = consolidada
    ? ['N°', 'Código', 'Empleado', 'Períodos', 'Días', 'Materia gravada', 'Personal', 'Patronal', 'IECE', 'SECAP', 'Total']
    : ['N°', 'Código', 'Empleado', 'Materia gravada', 'Personal', 'Patronal', 'IECE', 'SECAP', 'Total'];
  const MONEYCOLS = consolidada ? [5, 6, 7, 8, 9, 10] : [3, 4, 5, 6, 7, 8];
  const filasExport: Celda[][] = [
    ...filas.map((f, i): Celda[] => [
      i + 1, f.empleado_codigo, f.razon_social,
      ...(consolidada ? [Number(f.periodos), Number(f.dias)] : []),
      Number(f.base), Number(f.ap_personal), Number(f.ap_patronal), Number(f.iece), Number(f.secap), Number(f.total),
    ]),
    ['', '', 'TOTAL', ...(consolidada ? ['', ''] : []),
     tot('base'), tot('ap_personal'), tot('ap_patronal'), tot('iece'), tot('secap'), tot('total')],
  ];

  return (
    <div className="p-6 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold">Planillas IESS</h1>
        <div className="flex items-center gap-2">
          <select className={`${fieldCls} w-36`} value={mes} onChange={(e) => setMes(Number(e.target.value))} aria-label="Mes">
            {MESES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
          <input type="number" className={`${fieldCls} w-24`} value={anio} onChange={(e) => setAnio(Number(e.target.value))} aria-label="Año" />
          <TipoRolSelect value={tipo} onChange={setTipo} />
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        Aporte personal {pct ? Number(pct.personal) : 9.45}% (descontado al trabajador en el rol) + aporte patronal
        {' '}{pct ? Number(pct.patronal) : 11.15}% + IECE {pct ? Number(pct.iece) : 0.5}% + SECAP {pct ? Number(pct.secap) : 0.5}% (costo del empleador),
        sobre la materia gravada del rol del período. Los fondos de reserva se gestionan en Beneficios Sociales.
      </p>
      {consolidada && (
        <p className="text-xs text-muted-foreground">
          Esta empresa paga por período corto: cada línea <b>consolida los roles del mes</b> de esa persona,
          porque al IESS se le declara la aportación mensual. La columna «Períodos» dice cuántos se sumaron.
        </p>
      )}

      <AnexoDescarga tipo="sut" anio={anio} mes={mes}
        titulo={`Planilla del IESS ${MESES[mes - 1]} ${anio} — archivo para el SUT`} />

      {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}

      {!isFetching && !data?.rol && (
        <Card><CardContent className="py-10 text-center text-muted-foreground">
          No hay rol generado para {MESES[mes - 1]} {anio}.
        </CardContent></Card>
      )}

      {data?.rol && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card><CardContent className="py-3"><div className="text-xs text-muted-foreground">Rol {data.rol.codigo}</div><div className="text-lg font-semibold">{data.rol.estado}</div></CardContent></Card>
            <Card><CardContent className="py-3"><div className="text-xs text-muted-foreground">Aporte personal</div><div className="text-lg font-semibold font-mono tabular-nums">{money(tot('ap_personal'))}</div></CardContent></Card>
            <Card><CardContent className="py-3"><div className="text-xs text-muted-foreground">Aporte patronal + IECE + SECAP</div><div className="text-lg font-semibold font-mono tabular-nums">{money(tot('ap_patronal') + tot('iece') + tot('secap'))}</div></CardContent></Card>
            <Card><CardContent className="py-3"><div className="text-xs text-muted-foreground">Total planilla</div><div className="text-lg font-semibold font-mono tabular-nums">{money(tot('total'))}</div></CardContent></Card>
          </div>

          <Card>
            <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
              <CardTitle className="text-base">Detalle ({filas.length})</CardTitle>
              <ExportarBotones
                nombre={`planilla-iess-${fechaLocal()}`} titulo="Planillas IESS"
                headers={headersExp} filas={filasExport} colsDerecha={[0, ...MONEYCOLS]}
                fmtPDF={(v, c) => (MONEYCOLS.includes(c) ? (v === '' ? '' : money(Number(v))) : String(v ?? ''))}
                disabled={filas.length === 0} />
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b text-left text-xs text-muted-foreground">
                  <th className="py-2 pr-3">Código</th><th className="py-2 pr-3">Empleado</th>
                  {consolidada && <><th className="py-2 pr-3 text-right">Períodos</th><th className="py-2 pr-3 text-right">Días</th></>}
                  <th className="py-2 pr-3 text-right">Materia gravada</th><th className="py-2 pr-3 text-right">Personal</th>
                  <th className="py-2 pr-3 text-right">Patronal</th><th className="py-2 pr-3 text-right">IECE</th>
                  <th className="py-2 pr-3 text-right">SECAP</th><th className="py-2 pr-3 text-right">Total</th>
                </tr></thead>
                <tbody>
                  {filas.map((f, i) => (
                    <tr key={i} className="border-b last:border-0 hover:bg-muted/40">
                      <td className="py-2 pr-3 font-mono">{f.empleado_codigo}</td>
                      <td className="py-2 pr-3">{f.razon_social}<div className="text-xs text-muted-foreground font-mono">{f.identificacion}</div></td>
                      {consolidada && <>
                        <td className="py-2 pr-3 text-right tabular-nums">{f.periodos}</td>
                        <td className="py-2 pr-3 text-right tabular-nums">{f.dias}</td>
                      </>}
                      <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(f.base)}</td>
                      <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(f.ap_personal)}</td>
                      <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(f.ap_patronal)}</td>
                      <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(f.iece)}</td>
                      <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(f.secap)}</td>
                      <td className="py-2 pr-3 text-right font-mono tabular-nums font-semibold">{money(f.total)}</td>
                    </tr>
                  ))}
                  <tr className="font-semibold bg-muted/30">
                    <td className="py-2 pr-3" colSpan={consolidada ? 4 : 2}>TOTALES</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(tot('base'))}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(tot('ap_personal'))}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(tot('ap_patronal'))}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(tot('iece'))}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(tot('secap'))}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(tot('total'))}</td>
                  </tr>
                </tbody>
              </table>
            </CardContent>
          </Card>
        </>
      )}

      <Card>
        <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">SUT — Décimos (Ministerio del Trabajo) · {MESES[mes - 1]} {anio}</CardTitle>
          <ExportarBotones
            nombre={`sut-decimos-${anio}-${String(mes).padStart(2, '0')}`} titulo="SUT Décimos"
            headers={sutHeaders} filas={sutExport} colsDerecha={[0, ...sutMoney]}
            fmtPDF={(v, c) => (sutMoney.includes(c) ? (v === '' ? '' : money(Number(v))) : String(v ?? ''))}
            disabled={sutFilas.length === 0} />
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <p className="text-xs text-muted-foreground mb-3">
            Décimo tercero y décimo cuarto <b>mensualizados</b> (pagados en el rol) por trabajador, para el registro en el
            SUT. Exporta el archivo y mapéalo a la plantilla vigente del SUT (el orden de columnas del MDT cambia por año).
          </p>
          {sutFilas.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">Sin décimos registrados en {MESES[mes - 1]} {anio}.</div>
          ) : (
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-2 pr-3">Identificación</th><th className="py-2 pr-3">Apellidos y nombres</th>
                <th className="py-2 pr-3">Cargo</th><th className="py-2 pr-3 text-right">Décimo tercero</th>
                <th className="py-2 pr-3 text-right">Décimo cuarto</th><th className="py-2 pr-3">Forma</th>
              </tr></thead>
              <tbody>
                {sutFilas.map((f, i) => (
                  <tr key={i} className="border-b last:border-0 hover:bg-muted/40">
                    <td className="py-2 pr-3 font-mono">{f.cedula}</td>
                    <td className="py-2 pr-3">{f.nombres}</td>
                    <td className="py-2 pr-3 text-muted-foreground">{f.cargo}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(f.decimo_tercero)}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(f.decimo_cuarto)}</td>
                    <td className="py-2 pr-3 text-xs">{f.forma_pago}</td>
                  </tr>
                ))}
                <tr className="font-semibold bg-muted/30">
                  <td className="py-2 pr-3" colSpan={3}>TOTALES ({sutFilas.length})</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(sutTot('decimo_tercero'))}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(sutTot('decimo_cuarto'))}</td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
